/**
 * Input Component Styles
 * Includes Input, NumberInput, and Select variants
 */

import { mode } from '../../system/color-mode';

export const inputStyles = {
  // Base styles
  baseStyle: {
    field: {
      fontWeight: 400,
      borderRadius: '8px',
      width: '100%',
      minWidth: 0,
      outline: 0,
      position: 'relative',
      appearance: 'none',
      transitionProperty: 'common',
      transitionDuration: 'normal',
    },
    addon: {
      flex: '0 0 auto',
      width: 'auto',
      display: 'flex',
      alignItems: 'center',
      whiteSpace: 'nowrap',
    },
  },

  // Size variants
  sizes: {
    lg: {
      field: {
        fontSize: 'lg',
        px: 4,
        h: '48px',
        borderRadius: 'md',
      },
      addon: {
        fontSize: 'lg',
        px: 4,
        h: '48px',
        borderRadius: 'md',
      },
    },
    md: {
      field: {
        fontSize: 'md',
        px: 4,
        h: '40px',
        borderRadius: 'md',
      },
      addon: {
        fontSize: 'md',
        px: 4,
        h: '40px',
        borderRadius: 'md',
      },
    },
    sm: {
      field: {
        fontSize: 'sm',
        px: 3,
        h: '32px',
        borderRadius: 'sm',
      },
      addon: {
        fontSize: 'sm',
        px: 3,
        h: '32px',
        borderRadius: 'sm',
      },
    },
    xs: {
      field: {
        fontSize: 'xs',
        px: 2,
        h: '24px',
        borderRadius: 'sm',
      },
      addon: {
        fontSize: 'xs',
        px: 2,
        h: '24px',
        borderRadius: 'sm',
      },
    },
  },

  // Visual variants
  variants: {
    // Outline (default Chakra)
    outline: (props) => ({
      field: {
        border: '1px solid',
        borderColor: mode('gray.200', 'whiteAlpha.300')(props),
        bg: mode('white', 'whiteAlpha.100')(props),
        _hover: {
          borderColor: mode('gray.300', 'whiteAlpha.400')(props),
        },
        _focus: {
          zIndex: 1,
          borderColor: mode('brand.500', 'brand.300')(props),
          boxShadow: `0 0 0 1px ${mode('brand.500', 'brand.300')(props)}`,
        },
        _invalid: {
          borderColor: mode('red.500', 'red.300')(props),
          boxShadow: `0 0 0 1px ${mode('red.500', 'red.300')(props)}`,
        },
        _disabled: {
          opacity: 0.4,
          cursor: 'not-allowed',
        },
        _placeholder: {
          color: mode('gray.400', 'whiteAlpha.400')(props),
        },
      },
      addon: {
        border: '1px solid',
        borderColor: mode('gray.200', 'whiteAlpha.300')(props),
        bg: mode('gray.100', 'whiteAlpha.200')(props),
      },
    }),

    // Filled
    filled: (props) => ({
      field: {
        border: '2px solid',
        borderColor: 'transparent',
        bg: mode('gray.100', 'whiteAlpha.100')(props),
        _hover: {
          bg: mode('gray.200', 'whiteAlpha.200')(props),
        },
        _focus: {
          bg: 'transparent',
          borderColor: mode('brand.500', 'brand.300')(props),
        },
        _invalid: {
          borderColor: mode('red.500', 'red.300')(props),
        },
        _placeholder: {
          color: mode('gray.400', 'whiteAlpha.400')(props),
        },
      },
      addon: {
        border: '2px solid',
        borderColor: 'transparent',
        bg: mode('gray.100', 'whiteAlpha.200')(props),
      },
    }),

    // Flushed
    flushed: (props) => ({
      field: {
        borderBottom: '1px solid',
        borderColor: mode('gray.200', 'whiteAlpha.300')(props),
        borderRadius: 0,
        px: 0,
        bg: 'transparent',
        _focus: {
          borderColor: mode('brand.500', 'brand.300')(props),
          boxShadow: `0 1px 0 0 ${mode('brand.500', 'brand.300')(props)}`,
        },
        _invalid: {
          borderColor: mode('red.500', 'red.300')(props),
          boxShadow: `0 1px 0 0 ${mode('red.500', 'red.300')(props)}`,
        },
        _placeholder: {
          color: mode('gray.400', 'whiteAlpha.400')(props),
        },
      },
      addon: {
        borderBottom: '2px solid',
        borderColor: mode('gray.200', 'whiteAlpha.300')(props),
        borderRadius: 0,
        px: 0,
        bg: 'transparent',
      },
    }),

    // Unstyled
    unstyled: {
      field: {
        bg: 'transparent',
        px: 0,
        height: 'auto',
      },
      addon: {
        bg: 'transparent',
        px: 0,
        height: 'auto',
      },
    },

    // ==================== PROJECT CUSTOM VARIANTS ====================

    // Main
    main: (props) => ({
      field: {
        bg: mode('transparent', 'navy.800')(props),
        border: '1px solid',
        color: mode('secondaryGray.900', 'white')(props),
        borderColor: mode('secondaryGray.100', 'whiteAlpha.100')(props),
        borderRadius: '16px',
        fontSize: 'sm',
        p: '20px',
        _placeholder: { color: 'secondaryGray.400' },
        _focus: {
          borderColor: mode('brand.500', 'brand.400')(props),
        },
        _invalid: {
          borderColor: mode('red.500', 'red.300')(props),
        },
      },
    }),

    // Auth
    auth: (props) => ({
      field: {
        fontWeight: '500',
        color: mode('navy.700', 'white')(props),
        bg: mode('transparent', 'transparent')(props),
        border: '1px solid',
        borderColor: mode('secondaryGray.100', 'rgba(135, 140, 189, 0.3)')(props),
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600', fontWeight: '400' },
        _focus: {
          borderColor: mode('brand.500', 'brand.400')(props),
        },
      },
    }),

    // Auth Secondary
    authSecondary: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),

    // Search
    search: (props) => ({
      field: {
        border: 'none',
        py: '11px',
        borderRadius: 'inherit',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),

    // Transparent
    transparent: (props) => ({
      field: {
        bg: 'transparent',
        border: '0px solid',
        width: 'min-content',
        color: mode('secondaryGray.600', 'secondaryGray.600')(props),
        borderColor: 'transparent',
        padding: '0px',
        paddingLeft: '8px',
        paddingRight: '20px',
        fontWeight: '700',
        fontSize: '14px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),

    // Subtle
    subtle: (props) => ({
      box: {
        width: 'unset',
      },
      field: {
        bg: 'transparent',
        border: '0px solid',
        color: 'secondaryGray.600',
        borderColor: 'transparent',
        width: 'max-content',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),

    // Mini
    mini: (props) => ({
      field: {
        bg: mode('transparent', 'navy.800')(props),
        border: '0px solid transparent',
        fontSize: '0px',
        p: '10px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
  },

  // Default props
  defaultProps: {
    size: 'md',
    variant: 'outline',
  },
};

// NumberInput inherits from Input
export const numberInputStyles = {
  baseStyle: inputStyles.baseStyle,
  sizes: inputStyles.sizes,
  variants: {
    ...inputStyles.variants,
    // Override main variant for NumberInput
    main: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
    auth: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
    authSecondary: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
    search: (props) => ({
      field: {
        border: 'none',
        py: '11px',
        borderRadius: 'inherit',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
  },
  defaultProps: inputStyles.defaultProps,
};

// Select styles
export const selectStyles = {
  baseStyle: {
    field: {
      fontWeight: 400,
    },
    icon: {
      width: '1.5em',
      height: '100%',
      right: '0.5rem',
      position: 'relative',
      color: 'currentColor',
      fontSize: '1.25em',
    },
  },
  sizes: inputStyles.sizes,
  variants: {
    ...inputStyles.variants,
    // Override main for Select
    main: (props) => ({
      field: {
        bg: mode('transparent', 'navy.800')(props),
        border: '1px solid',
        color: 'secondaryGray.600',
        borderColor: mode('secondaryGray.100', 'whiteAlpha.100')(props),
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
      icon: {
        color: 'secondaryGray.600',
      },
    }),
    mini: (props) => ({
      field: {
        bg: mode('transparent', 'navy.800')(props),
        border: '0px solid transparent',
        fontSize: '0px',
        p: '10px',
        _placeholder: { color: 'secondaryGray.600' },
      },
      icon: {
        color: 'secondaryGray.600',
      },
    }),
    subtle: (props) => ({
      box: {
        width: 'unset',
      },
      field: {
        bg: 'transparent',
        border: '0px solid',
        color: 'secondaryGray.600',
        borderColor: 'transparent',
        width: 'max-content',
        _placeholder: { color: 'secondaryGray.600' },
      },
      icon: {
        color: 'secondaryGray.600',
      },
    }),
    transparent: (props) => ({
      field: {
        bg: 'transparent',
        border: '0px solid',
        width: 'min-content',
        color: mode('secondaryGray.600', 'secondaryGray.600')(props),
        borderColor: 'transparent',
        padding: '0px',
        paddingLeft: '8px',
        paddingRight: '20px',
        fontWeight: '700',
        fontSize: '14px',
        _placeholder: { color: 'secondaryGray.600' },
      },
      icon: {
        transform: 'none !important',
        position: 'unset !important',
        width: 'unset',
        color: 'secondaryGray.600',
        right: '0px',
      },
    }),
    auth: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
    authSecondary: (props) => ({
      field: {
        bg: 'transparent',
        border: '1px solid',
        borderColor: 'secondaryGray.100',
        borderRadius: '16px',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
    search: (props) => ({
      field: {
        border: 'none',
        py: '11px',
        borderRadius: 'inherit',
        _placeholder: { color: 'secondaryGray.600' },
      },
    }),
  },
  defaultProps: inputStyles.defaultProps,
};

export default inputStyles;
